/**
 * AgentPickerModal
 *
 * The chooser behind "continue with another agent". Deliberately mirrors the
 * Agents tab card (avatar + name + description on a rounded tile) so picking an
 * agent here feels like the same surface the user already knows, just scoped to
 * a handoff.
 *
 * The agent currently driving the conversation is shown but not selectable —
 * branching to yourself is what the plain fork button already does, and hiding
 * the card entirely would make the list silently disagree with the Agents tab.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { BotAvatar } from '@/components/common/BotAvatar';
import { useLanguage } from '@/hooks/useLanguage';
import botService from '@modules/chatbots/services/botService';
import type { Bot } from '@/types/models';
import './AgentPickerModal.css';

/** How the handed-off conversation should open. */
export type HandoffOpenMode = 'jump' | 'split';

/** Remembered across handoffs — the choice is a working preference, not a
 *  per-click decision, so re-picking it every time would be busywork. */
const OPEN_MODE_KEY = 'agentHandoffOpenMode';

function readOpenMode(): HandoffOpenMode {
  try {
    const v = window.localStorage.getItem(OPEN_MODE_KEY);
    return v === 'split' ? 'split' : 'jump';   // jump is the default
  } catch {
    return 'jump';
  }
}

interface AgentPickerModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** Chosen agent + how to open it — the caller performs the actual handoff. */
  onSelect: (bot: Bot, mode: HandoffOpenMode) => void;
  /** True when the split rail is full. The split option is then offered but
   *  disabled, rather than letting the user choose it and fail afterwards. */
  splitDisabled?: boolean;
  /** Tooltip explaining why split is unavailable. */
  splitDisabledReason?: string;
  /** Agent currently driving the conversation; rendered with a "current" chip
   *  and made non-selectable. */
  currentChatbotId?: string | null;
  /** Name of the agent we're branching FROM, shown in the subtitle so the
   *  modal states both ends of the handoff. */
  currentChatbotName?: string | null;
}

export function AgentPickerModal({
  isOpen,
  onClose,
  onSelect,
  splitDisabled = false,
  splitDisabledReason,
  currentChatbotId,
  currentChatbotName,
}: AgentPickerModalProps) {
  const { t } = useLanguage();
  const [openMode, setOpenMode] = useState<HandoffOpenMode>(readOpenMode);

  const chooseMode = (mode: HandoffOpenMode) => {
    if (mode === 'split' && splitDisabled) return;
    setOpenMode(mode);
    try { window.localStorage.setItem(OPEN_MODE_KEY, mode); } catch { /* private mode */ }
  };

  // If the rail fills up while the chooser is open, a remembered 'split'
  // preference would silently fail on click — fall the UI back to jump.
  useEffect(() => {
    if (splitDisabled && openMode === 'split') setOpenMode('jump');
  }, [splitDisabled, openMode]);
  const [bots, setBots] = useState<Bot[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [query, setQuery] = useState('');
  const searchRef = useRef<HTMLInputElement>(null);

  // Fetch on every open rather than once on mount: agents can be created,
  // renamed or shared between two opens of this modal, and a stale chooser
  // that omits a brand-new agent is a confusing failure.
  useEffect(() => {
    if (!isOpen) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    // getBots(), not getCarouselBots(): the carousel endpoint returns a
    // favorites/recents SHORTLIST (three cards in a tenant with a hundred
    // agents). A handoff chooser has to offer every agent the user can reach,
    // the same set the Agents tab shows.
    botService
      .getBots()
      .then(data => {
        if (cancelled) return;
        setBots(data || []);
      })
      .catch(() => {
        if (cancelled) return;
        setError(t('chat.agentBranch.loadFailed') || 'Could not load your agents.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [isOpen, t]);

  // Reset the query and focus the search each time the modal opens, so a
  // second handoff doesn't start inside the previous search's results.
  useEffect(() => {
    if (!isOpen) return;
    setQuery('');
    const id = window.setTimeout(() => searchRef.current?.focus(), 60);
    return () => window.clearTimeout(id);
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [isOpen, onClose]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return bots;
    return bots.filter(b =>
      (b.chatbot_name || '').toLowerCase().includes(q) ||
      (b.chatbot_description || '').toLowerCase().includes(q)
    );
  }, [bots, query]);

  if (!isOpen) return null;

  return createPortal(
    <div
      className="agent-picker-overlay"
      onClick={onClose}
      role="presentation"
    >
      <div
        className="agent-picker-modal"
        role="dialog"
        aria-modal="true"
        aria-label={t('chat.agentBranch.pickerTitle') || 'Continue with another agent'}
        onClick={e => e.stopPropagation()}
      >
        <header className="agent-picker-header">
          <div className="agent-picker-heading">
            <h2 className="agent-picker-title">
              {t('chat.agentBranch.pickerTitle') || 'Continue with another agent'}
            </h2>
            <p className="agent-picker-subtitle">
              {currentChatbotName
                ? (t('chat.agentBranch.pickerSubtitleFrom') || 'This conversation carries over from {{agent}}.')
                    .replace('{{agent}}', currentChatbotName)
                : (t('chat.agentBranch.pickerSubtitle') || 'The full conversation so far carries over.')}
            </p>
          </div>
          <button
            type="button"
            className="agent-picker-close"
            onClick={onClose}
            aria-label={t('common.close') || 'Close'}
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </header>

        {/* How the handed-off conversation opens. Sits above the list because
            it applies to whichever agent is picked next, and is remembered so
            a user with a settled preference never thinks about it again. */}
        <div className="agent-picker-mode" role="radiogroup"
             aria-label={t('chat.agentBranch.openModeLabel') || 'How to open'}>
          <span className="agent-picker-mode-label">
            {t('chat.agentBranch.openModeLabel') || 'How to open'}
          </span>
          <div className="agent-picker-mode-options">
            <button
              type="button"
              role="radio"
              aria-checked={openMode === 'jump'}
              className={`agent-picker-mode-btn ${openMode === 'jump' ? 'is-active' : ''}`}
              onClick={() => chooseMode('jump')}
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <line x1="5" y1="12" x2="19" y2="12" />
                <polyline points="13 6 19 12 13 18" />
              </svg>
              {t('chat.agentBranch.modeJump') || 'Jump to the agent'}
            </button>
            <button
              type="button"
              role="radio"
              aria-checked={openMode === 'split'}
              className={`agent-picker-mode-btn ${openMode === 'split' ? 'is-active' : ''}`}
              onClick={() => chooseMode('split')}
              disabled={splitDisabled}
              aria-disabled={splitDisabled}
              title={splitDisabled
                ? (splitDisabledReason || t('chat.branchLimitTooltip') || '')
                : undefined}
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <rect x="3" y="4" width="18" height="16" rx="2" />
                <line x1="12" y1="4" x2="12" y2="20" />
              </svg>
              {t('chat.agentBranch.modeSplit') || 'Open in split screen'}
            </button>
          </div>
        </div>

        <div className="agent-picker-search">
          <svg className="agent-picker-search-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
            <circle cx="11" cy="11" r="8" />
            <path d="M21 21l-4.35-4.35" />
          </svg>
          <input dir="auto"
            ref={searchRef}
            type="text"
            className="agent-picker-search-input"
            placeholder={t('chat.agentBranch.searchPlaceholder') || 'Search agents...'}
            value={query}
            onChange={e => setQuery(e.target.value)}
          />
        </div>

        <div className="agent-picker-body">
          {loading ? (
            <div className="agent-picker-state">
              <div className="agent-picker-spinner" />
              <span>{t('common.loading') || 'Loading...'}</span>
            </div>
          ) : error ? (
            <div className="agent-picker-state agent-picker-state-error">{error}</div>
          ) : filtered.length === 0 ? (
            <div className="agent-picker-state">
              {t('chat.agentBranch.noResults') || 'No agents match your search.'}
            </div>
          ) : (
            <div className="agent-picker-grid">
              {filtered.map(bot => {
                const isCurrent = !!currentChatbotId && bot.chatbot_id === currentChatbotId;
                return (
                  <button
                    key={bot.chatbot_id}
                    type="button"
                    className={`agent-picker-card ${isCurrent ? 'is-current' : ''}`}
                    onClick={() => !isCurrent && onSelect(bot, openMode)}
                    disabled={isCurrent}
                    aria-disabled={isCurrent}
                    title={isCurrent
                      ? (t('chat.agentBranch.currentAgent') || 'Current agent')
                      : bot.chatbot_name}
                  >
                    <div className="agent-picker-card-header">
                      <BotAvatar
                        name={bot.chatbot_name || 'AI'}
                        size={32}
                        colorOverride={(bot as any).avatar_color}
                        variantOverride={(bot as any).avatar_variant ?? undefined}
                      />
                      <span className="agent-picker-card-name bidi-clamp" dir="auto">{bot.chatbot_name}</span>
                    </div>
                    <span className="agent-picker-card-desc bidi-clamp" dir="auto">
                      {bot.chatbot_description || (t('agents.noDescription') || 'No description')}
                    </span>
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
}

export default AgentPickerModal;
